import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLang } from '../hooks/useLang';
import { INSTALL_HINT_KEY, useInstallPrompt } from '../hooks/useInstallPrompt';
import { t } from '../../i18n';
import type { MessageKey } from '../../i18n';
import type { ViewId } from '../../types';

const STEPS: readonly MessageKey[] = ['home.howto.step1', 'home.howto.step2', 'home.howto.step3'];

/** Matches `[label:target]` inside a translated string — the sentence stays
 *  one flowing, grammatically correct string per language, with the linked
 *  word(s) marked inline rather than split into separate before/after
 *  translation keys (which would fix the word order to English's). */
const LINK_RE = /\[([^\]:]+):([a-z]+)\]/g;

/** Renders a string built from `LINK_RE` markers, turning every `[label:target]`
 *  into a button that runs `onAction(target)` — a screen jump, or (for the
 *  import/export paragraph below) the import/export action itself. */
function LinkedText({ text, onAction }: { text: string; onAction: (target: string) => void }) {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(LINK_RE)) {
    const [whole, label, target] = match;
    const index = match.index;
    if (index > lastIndex) nodes.push(text.slice(lastIndex, index));
    nodes.push(
      <button
        key={index}
        type="button"
        className="font-medium underline underline-offset-2 hover:no-underline"
        onClick={() => onAction(target)}
      >
        {label}
      </button>,
    );
    lastIndex = index + whole.length;
  }
  nodes.push(text.slice(lastIndex));
  return <>{nodes}</>;
}

/**
 * The default landing screen — shown when no other screen has been picked
 * yet (empty/unrecognized `location.hash`) and reachable at any time by
 * clicking the "Kür" title in the header bar or the desktop nav sidebar
 * (see AppBar.tsx / TabBar.tsx). Deliberately has no nav anchor of its own —
 * it's a jumping-off point, not one of the seven step screens — so it's not
 * in `NAV`/`STEP_ORDER` and carries no "Step N" eyebrow.
 */
export function HomeView({
  hidden,
  onNavigate,
  onOpenPrivacyPolicy,
  onOpen,
  onSave,
  onImportSelection,
  onExportSelection,
}: {
  hidden: boolean;
  onNavigate: (id: ViewId) => void;
  onOpenPrivacyPolicy: () => void;
  onOpen: () => void;
  onSave: () => void;
  onImportSelection: () => void;
  onExportSelection: () => void;
}) {
  useLang();
  const { platform, installed, canPrompt, promptInstall } = useInstallPrompt();

  function handleImportExportAction(target: string): void {
    switch (target) {
      case 'open':
        onOpen();
        break;
      case 'save':
        onSave();
        break;
      case 'import':
        onImportSelection();
        break;
      case 'export':
        onExportSelection();
        break;
      default:
        onNavigate(target as ViewId);
    }
  }

  return (
    <section className="mx-auto flex max-w-[720px] flex-col gap-4 p-4" hidden={hidden}>
      <div className="flex flex-col gap-1.5 py-2">
        <h2 className="text-3xl font-bold tracking-tight text-foreground">{t('home.welcome.title')}</h2>
        <p className="max-w-prose text-sm text-muted-foreground">{t('home.welcome.text')}</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('home.howto.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          <ol className="flex flex-col gap-1.5 pl-1 text-sm">
            {STEPS.map((key, i) => (
              <li key={key} className="flex gap-2">
                <span className="font-mono text-xs text-muted-foreground">{i + 1}.</span>
                <span>
                  <LinkedText text={t(key)} onAction={(target) => onNavigate(target as ViewId)} />
                </span>
              </li>
            ))}
          </ol>
          <Button className="self-start" onClick={() => onNavigate('registrationData')}>
            {t('home.start')}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('home.importExport.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            <LinkedText text={t('home.importExport.text')} onAction={handleImportExportAction} />
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('home.privacy.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">{t('home.privacy.text')}</p>
          <button
            type="button"
            className="self-start text-left text-sm font-medium text-brand underline underline-offset-2 hover:no-underline"
            onClick={onOpenPrivacyPolicy}
          >
            {t('consent.privacyLink')}
          </button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('home.install.title')}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-prose text-sm text-muted-foreground">
            {installed ? t('home.install.already') : t(canPrompt ? 'home.install.prompt' : INSTALL_HINT_KEY[platform])}
          </p>
          {!installed && canPrompt && (
            <Button className="shrink-0 self-start" onClick={() => void promptInstall()}>
              {t('home.install.button')}
            </Button>
          )}
        </CardContent>
      </Card>
    </section>
  );
}
